import BoardCard from "@/components/BoardCard";
import HBList from "@/components/HBList";
import HBMessage from "@/components/HBMessage";
import useAppStore from "@/context/store";
import { getRecycleBoards, recoverBoard } from "@/services";
import { Button } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "../index.module.less";
import Empty from "./Empty";
import TabPanel from "./TabPanel";
function RecycleBoardCard({ style, className, record, urlname, onRecover }) {
  const [loading, setLoading] = useState(false);
  const imgs = useMemo(() => {
    const list = [];
    if (record.cover?.file) {
      list.push(record.cover.file);
    }

    record.pins?.slice(0, 4).forEach((pin) => {
      list.push({ ...pin.file, raw_text: pin?.raw_text });
    });

    return list;
  }, [record]);

  // 0：公开
  // 1：管理员设为私密，前端当做0处理
  // 2：待分类
  // 3：私密
  const isPrivate = useMemo(() => {
    return record?.is_private !== 0 && record?.is_private !== 1;
  }, [record]);

  const onRecovery = useCallback(
    async (evt) => {
      evt.stopPropagation();
      setLoading(true);
      const result = await recoverBoard({
        board_id: record.board_id,
        urlname,
      });
      setLoading(false);
      if (!result.err) {
        HBMessage.success({
          content: (
            <div className={styles.message}>
              <div className={styles.title}>画板已恢复</div>
              <a
                className={styles.link}
                href={`/user/${urlname}`}
                target="_blank"
              >
                查看
              </a>
            </div>
          ),
        });
        onRecover && onRecover(record);
      }
    },
    [onRecover, record, urlname]
  );

  const onTip = useCallback(() => {
    HBMessage.open("在回收站内无法查看详情");
  }, []);

  return (
    <div
      className={classNames([styles.card, className])}
      style={style}
      onClick={onTip}
    >
      <BoardCard
        className={styles.boardCard}
        record={record}
        boardType={"new"}
        disableHoverMask
        noClick
        hideUser
      />
      <Button
        className={styles.button}
        loading={loading}
        data-gd-click="button_click"
        data-button-name="恢复画板"
        onClick={onRecovery}
      >
        恢复
      </Button>
    </div>
  );
}

function RecycleBoardList({ dataSource, urlname, onRecover, ...props }) {
  const column = useAppStore(({ column }) => column);
  const indexRef = useRef(0);

  const render = useCallback(
    (record) => {
      if (record.type === "date") {
        indexRef.current = 0;
        return (
          <div className={styles.date} key={record.board_id}>
            {record.title}
          </div>
        );
      } else {
        return (
          <RecycleBoardCard
            className={styles.card}
            style={
              ++indexRef.current % column !== 0 ? { marginRight: "16px" } : null
            }
            key={record.board_id}
            record={record}
            urlname={urlname}
            onRecover={onRecover}
          />
        );
      }
    },
    [column, onRecover, urlname]
  );

  return (
    <HBList
      disableMasonry={true}
      {...props}
      loginGuide={true}
      uniqueKey="board_id"
      render={render}
      dataSource={dataSource}
      endMessage={<div className={styles.end}>仅保留最近7天的删除记录</div>}
    />
  );
}

const today = dayjs().format("YYYY/MM/DD");
const yeterday = dayjs().subtract(1, "day").format("YYYY/MM/DD");

// 增加时间数据，并且拍平插入到dataSource中
const flattenGroupDataSource = (dataSource) => {
  const groupByDateDataSource = dataSource.reduce((result, record) => {
    const date = dayjs(record.deleted_at * 1000).format("YYYY/MM/DD");
    if (result[date]) {
      result[date].push(record);
    } else {
      result[date] = [record];
    }
    return result;
  }, {});
  return Object.entries(groupByDateDataSource).reduce(
    (result, [date, group]) => {
      let title = date;
      if (date === today) {
        title = `今天 ${date}`;
      } else if (date === yeterday) {
        title = `昨天 ${date}`;
      }
      result.push({
        type: "date",
        title,
        board_id: `date${date}`,
      });
      result = result.concat(group);
      return result;
    },
    []
  );
};

function UserRecycleBoardList({ urlname }) {
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const pageRef = useRef(1);

  const onRecover = useCallback((board) => {
    setDataSource((dataSource) =>
      flattenGroupDataSource(
        dataSource.filter(
          (record) =>
            record.type !== "date" && record.board_id !== board.board_id
        )
      )
    );
  }, []);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setHasMore(true);
    setDataSource([]);

    return async () => {
      const result = await getRecycleBoards({
        urlname,
        page: pageRef.current,
        limit: 20,
      });
      const boards = result?.boards || [];

      if (boards.length <= 0) {
        setHasMore(false);
      }

      setDataSource((dataSource) => {
        const newDataSource = dataSource.concat(boards);
        return flattenGroupDataSource(
          newDataSource.filter((record) => record.type !== "date")
        );
      });
    };
  }, [urlname]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    pageRef.current += 1;
    queryAction();
  }, [queryAction]);

  return (
    <If condition={dataSource.length > 0 || hasMore}>
      <Then>
        <RecycleBoardList
          onRecover={onRecover}
          dataSource={dataSource}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          urlname={urlname}
        />
      </Then>
      <Else>
        <Empty
          imageUrl={
            "https://cdn.dancf.com/fe-assets/img/a52cf2370b7c7f53c751dfcd154fcbde.png"
          }
          title="回收站什么都没有"
        />
      </Else>
    </If>
  );
}

type IRecycleListPanel = {
  urlname: string;
  isMe: boolean;
  userStore: {
    [key: string]: any;
  };
};

export default function RecycleListPanel({ urlname }: IRecycleListPanel) {
  return (
    <TabPanel>
      <div style={{ marginTop: "32px" }}>
        <UserRecycleBoardList urlname={urlname} />
      </div>
    </TabPanel>
  );
}
